/* eslint-disable prefer-destructuring */
/* eslint-disable @typescript-eslint/naming-convention */
import { v4 as uuid } from 'uuid';
import { makeAutoObservable, reaction, runInAction } from 'mobx';
import { NavigateFunction } from 'react-router-dom';
import {
  clearPersistedStore,
  isPersisting,
  makePersistable,
} from 'mobx-persist-store';
import { AxiosError, isAxiosError } from 'axios';
import { t } from 'i18next';
import _ from 'lodash';
import {
  CountryDetails,
  DeliveryOptionsRequest,
  DeliveryRoute,
  ParcelDimensions,
} from '../models/shipmentCountryInfo';
import agent from '../api/agent';
import {
  Address,
  CourierPickupSlot,
  InsuranceCoverage,
  ServiceType,
  Shipment,
} from '../models/parcelDeliveryMiles';
import { FirstMile, LastMile } from '../models/delivery-options/delivery-miles';
import { tryGetErrorMessage } from '../utils/parcel-creation/parcel-creation-utils';
import { StoreDeliveryOptionsRequest } from '../models/storeDeliveryOptions';
import {
  contactDetailsValidationSchema,
  emailValidationSchema,
} from '../routes/personal-data/contact-details-form.component';
import { PudoFilters, PudoSearch } from '../models/pudoSearchRequest';
import { Geocode, PudoItem, PudoSearchMeta } from '../models/pudoItem';
import {
  getAddressComponentsByZipCode,
  getCityByZipCode,
} from '../utils/google-places/google-places.utils';
import {
  getCurrentTimezoneOffset,
  handleError,
  removeDuplicates,
} from '../utils/generic/generic.utils';
import { store } from './store';
import { ParcelItem, HsCodeRequest } from '../models/hsCode';
import {
  getCourierAddressValidationSchema,
  UserRole,
} from '../features/parcel-creation/delivery-details-form/delivery-details-form.component';
import { AppRoute } from '../constants/route';
import { SubmitParcelRequest } from '../models/submitParcelRequest';
import { InsuranceLimits } from '../models/insuranceLimits';
import { Phone } from '../models/user';
import { PickupDateTimeSlots } from '../models/pickupWindowsModels';
import {
  IDeliveryLimitations,
  RouteNotFoundError,
} from '../models/parcelDimensions';
import { calculateVolumetricWeight } from '../features/parcel-creation/parcel-details-dimensions/dimensionsConfig';
import { ApiResponseStatus } from '../constants/server';
import { ICalculatorData } from './widgetCalculatorStore';
import { convertDimensions } from '../utils/parcel-measurements.ts/parcel-measurements-utils';
import {
  movePointerBack,
  movePointerForward,
  ParcelCreationNavigation,
  Step,
  StepName,
} from '../models/parcelCreationFlowNavigation';
import {
  getSetting,
  getSettingReceiverCountry,
} from '../utils/countries/countries.utils';
import { CountriesResponse } from '../models/countriesModel';
import {
  ApiResponse,
  DelivertyOptionsResponse,
  HscodeSearchRequestData,
  MessageBus,
  ShipmentResponseData,
} from '../models/apiResponse';
import {
  BusMessageHandler,
  handleBusMessages,
} from '../utils/message-bus/message-bus-utils';
import { DEFAULT_COUNTRY } from './localizationsStore';
import { StoreCourierRecipientRequest } from '../models/recipient-details/store-courier-recipient-request';
import { StorePudoRecipientRequest } from '../models/recipient-details/store-pudo-recipient-request';
import {
  AddressFields,
  ContactInfoFields,
  CourierPickupFields,
  PudoFields,
} from '../models/recipient-details/common-fields';
import { ToastContent } from '../common/toasts/toast-message';
import { DeliveryOption } from '../models/delivery-options/delivery-option';
import { compareMiles } from '../features/parcel-creation/parcel-delivery-option/helpers';
import { getDefaultMile } from '../routes/parcel-delivery/helpers';
import { IGenericInfoBlock } from '../models/generic-info-block/generic-info-block';
import { DeliveryMethod, PriceRange } from '../models/delivery-options/common';
import { prepareEcommerceItem } from '../services/analytics/helpers';
import { getGAClientAndSession } from '../services/analytics/ga-client-session';
import {
  checkRescheduleRequired,
  handleReschedule,
} from '../utils/payments/payments.utils';
import { translate } from '../../i18n';
import { isFeatureRequired } from '../utils/client/client.utils';

const defaultNavigationMap: Step[] = [
  { name: 'parcel-details', type: '', elements: [] },
  { name: 'delivery-option', type: '', elements: [] },
];

const signatureStep: Step = {
  name: AppRoute.SIGNATURE,
  type: '',
  elements: [],
};

const agentFlowDeliveryInstructions: Step = {
  name: 'delivery-instructions',
  type: '',
  elements: [],
};

const defaultNavigation: ParcelCreationNavigation = {
  map: defaultNavigationMap,
  current: defaultNavigationMap[0],
  next: defaultNavigationMap[1],
  previous: null,
};

export type ShippingType = 'international' | 'domestic';

export type TRecaptchaToken = string | null;

interface GetPudosForRoleProps {
  role?: UserRole;
  useCache?: boolean;
  searchString?: string;
}

export interface CheckboxValues {
  [k: string]: boolean | string | undefined;
}

export default class ParcelCreationFlowStore {
  isParcelCreationInProgress: boolean = false;

  hasPresentedAiBetaPanel: boolean = false;

  isLoadingShipmentApi: boolean = false;

  isLoadingSearchApi: boolean = false;

  isLoadingTaxes: boolean = false;

  savedSteps: StepName[] = []; // array which defines which steps were already sent to backend

  countryDeparture: CountryDetails | null = null;

  countryDestination: CountryDetails | null = null;

  parcelDimensions: ParcelDimensions | null = null;

  nonStandardPackaging: boolean = false;

  deliveryOptions: DeliveryOption[] | undefined = undefined;

  groupedDeliveryPrices: Record<DeliveryMethod, PriceRange> | undefined =
    undefined;

  staticDeliveryTimeStrings: Record<DeliveryMethod, string> | undefined =
    undefined;

  selectedDeliveryOption: DeliveryOption | undefined = undefined;

  selectedMiddleMileFilter: DeliveryMethod | undefined;

  deliveryOptionHints: IGenericInfoBlock | undefined = undefined;

  selectedSenderPudo: PudoItem | undefined = undefined;

  selectedReceiverPudo: PudoItem | undefined = undefined;

  shipment: Shipment | undefined = undefined;

  foundPudoItems: PudoItem[] | undefined = undefined;

  foundPudoMeta: PudoSearchMeta | undefined = undefined;

  preservePudos: boolean = false;

  pudoFilterTimeWeight: PudoFilters | undefined = undefined;

  pudoFilterGeocode: Geocode | undefined = undefined;

  pudoFilterDistance: number | undefined = undefined;

  pudoFilterSearch: string | undefined = undefined;

  summaryConfirmFormValues: CheckboxValues | undefined = undefined;

  summaryFormIsValid: boolean = false;

  signature: string | undefined = undefined;

  insuranceLimits: InsuranceLimits | undefined = undefined;

  taxesFee: number = 0; // currently hardcoded

  currentLocation: Geocode | undefined = undefined;

  navigation: ParcelCreationNavigation = { ...defaultNavigation };

  shippingType: ShippingType = 'international';

  createdParcelPaymentLink: string | undefined = undefined;

  phoneVerificationCode: string | undefined;

  isPhoneCodeValid: boolean = true;

  isPhoneVerified: boolean = false;

  guestRecaptchaToken: TRecaptchaToken = null;

  isNextStepButtonLoading: boolean = false;

  isRouteExpectationLoading: boolean = false;

  dateInfo: PickupDateTimeSlots[] | undefined = undefined;

  isLoadingApiCallCourier: boolean = false;

  isLoadingApiStorePickupWindow: boolean = false;

  cachedTimezone: string | null = null;

  deliveryLimitations: IDeliveryLimitations | undefined = undefined;

  routeNotFoundError: RouteNotFoundError | undefined = undefined;

  shouldShowManual: boolean = false;

  isDeliveryOptionsFailed: boolean = false;

  taxesInfo: IGenericInfoBlock | undefined = undefined;

  isDraftEditing: boolean = false;

  isDomestic: boolean = false;

  // parcel, document
  shipmentType: string | undefined = undefined;

  isRateSchemaChanged: boolean = false;

  noDeliveryOptionAvailable: boolean = false;

  prefillInProgress: boolean = false;

  isNextButtonClicked: { value: boolean } = { value: false };

  isSignButtonClicked: boolean = false;

  omitLimitationsRequest: boolean = false;

  senderAddressInvalid: boolean = false;

  domesticInsurance: number | undefined = undefined;

  isSenderPersonalDataValid: boolean = false;

  isReceiverPersonalDataValid: boolean = false;

  recipientPersonalDataFormValues: Record<string, string | null> | undefined =
    undefined;

  senderPersonalDataFormValues: Record<string, string | null> | undefined =
    undefined;

  setShippingType = (type: ShippingType) => {
    this.shippingType = type;
  };

  setSenderAddressInvalid = (value: boolean) => {
    this.senderAddressInvalid = value;
  };

  getContactDetailsValidationSchema = (senderType: 'sender' | 'receiver') => {
    const isEmailOptional =
      senderType === 'receiver' &&
      this.isDomestic &&
      isFeatureRequired(
        'optionalEmailDomesticeReceiverAllowed',
        this.countryDestination?.code
      );

    return isEmailOptional
      ? contactDetailsValidationSchema.shape({
          email: emailValidationSchema.notRequired(),
        })
      : contactDetailsValidationSchema;
  };

  get isDocumentShipmentType() {
    return this.shipmentType === 'document';
  }

  get showStep0Insurance() {
    return this.isDocumentShipmentType || this.isDomestic;
  }

  get countAppliedFilters() {
    let count = 0;

    if (this.pudoFilterDistance) count += 1;
    if (this.pudoFilterTimeWeight?.weight) count += 1;
    if (this.pudoFilterTimeWeight?.open24_7) count += 1;
    if (this.pudoFilterTimeWeight?.open_now) count += 1;
    if (this.pudoFilterTimeWeight?.open_on_weekends) count += 1;

    return count;
  }

  get canSkipVerifyPhone() {
    return this.isPhoneVerified;
  }

  get matchedCountry(): CountriesResponse | undefined {
    if (!this.countryDeparture) return undefined;

    const availableCountries =
      store.localizationsStore.availableCountries ?? [];

    return availableCountries.find(
      (country) =>
        country.country_code ===
        store.localizationsStore.selectedCountry?.country_code
    );
  }

  get currentElements() {
    return this.navigation.current?.elements ?? [];
  }

  get isAgentFlowShowMap() {
    const firstMileServiceType: ServiceType = this.selectedDeliveryOption
      ?.selectedFirstMile?.service.code as ServiceType;
    const res =
      firstMileServiceType === 'agent' &&
      getSetting(
        this.countryDeparture?.code,
        'delivery_options',
        'meest_agent_flow'
      ) === 'show_map';
    return res;
  }

  get isSignatureRequired() {
    return this.navigation.map.some(
      (s) =>
        s.name === 'summary' &&
        s.elements.some(
          (e) => e.name === 'signature' && e.type === 'input-signature'
        )
    );
  }

  get isFinishWithPay() {
    return this.navigation.map.some(
      (s) =>
        s.name === 'summary' &&
        s.elements.some(
          (e) =>
            e.name === 'action-pay-parcel' && e.type === 'action-pay-parcel'
        )
    );
  }

  get promocode() {
    return Array.isArray(this.shipment?.promocodes) &&
      this.shipment.promocodes.length > 0
      ? this.shipment.promocodes[0]
      : undefined;
  }

  constructor() {
    makeAutoObservable(this);
    makePersistable(this, {
      name: 'ParcelCreationFlowStore',
      properties: [
        'savedSteps',
        'countryDestination',
        'countryDeparture',
        'parcelDimensions',
        'nonStandardPackaging',
        'deliveryOptions',
        'staticDeliveryTimeStrings',
        'groupedDeliveryPrices',
        'selectedDeliveryOption',
        'selectedMiddleMileFilter',
        'deliveryOptionHints',
        'selectedSenderPudo',
        'selectedReceiverPudo',
        'shipment',
        'foundPudoItems',
        'pudoFilterTimeWeight',
        'pudoFilterGeocode',
        'pudoFilterDistance',
        'summaryConfirmFormValues',
        'signature',
        'insuranceLimits',
        'taxesFee',
        'currentLocation',
        'navigation',
        'isPhoneVerified',
        'guestRecaptchaToken',
        'isParcelCreationInProgress',
        'hasPresentedAiBetaPanel',
        'deliveryLimitations',
        'isDeliveryOptionsFailed',
        'isDraftEditing',
        'isDomestic',
        'domesticInsurance',
        'foundPudoMeta',
        'isRateSchemaChanged',
        'noDeliveryOptionAvailable',
        'isReceiverPersonalDataValid',
        'isSenderPersonalDataValid',
        'recipientPersonalDataFormValues',
        'senderPersonalDataFormValues',
        'routeNotFoundError',
        'shipmentType',
      ],
      storage: window.sessionStorage,
    });

    //  this reaction is required for cases when pudo is pre-filled by backend
    reaction(
      () => this.shipment?.sender?.carrier.pudo_info,
      () => {
        runInAction(() => {
          if (this.shipment?.sender?.carrier.pudo_info)
            this.setSenderPudo(this.shipment.sender.carrier.pudo_info);
        });
      }
    );

    reaction(
      () => [this.countryDeparture?.code, this.countryDestination?.code],
      () => {
        if (this.countryDeparture?.code === this.countryDestination?.code) {
          this.setIsDomestic(true);
        } else {
          this.setIsDomestic(false);
          this.insuranceLimits = undefined;
          this.setDomesticInsurance(undefined);
        }
      }
    );
  }

  setIsRateSchemaChanged = (value: boolean) => {
    this.isRateSchemaChanged = value;
  };

  setNoDeliveryOptionAvailable = (value: boolean) => {
    this.noDeliveryOptionAvailable = value;
  };

  setGuestRecaptchaToken = (value: TRecaptchaToken) => {
    this.guestRecaptchaToken = value;
  };

  markStepAsSaved = (stepName: StepName) => {
    if (!this.savedSteps.includes(stepName)) {
      this.savedSteps.push(stepName);
    }
  };

  removeStepFromSaved = (stepName: StepName) => {
    this.savedSteps = this.savedSteps.filter((i) => i !== stepName);
  };

  isStepSaved = (stepName: StepName) => this.savedSteps.includes(stepName);

  getShipmentDetails = async (shipmentId: number) => {
    this.isLoadingShipmentApi = true;

    try {
      const { shipment } = await agent.Shipment.summary(shipmentId);

      runInAction(() => {
        this.shipment = shipment;
      });

      return shipment;
    } catch (error) {
      handleError(error);
    } finally {
      runInAction(() => {
        this.isLoadingShipmentApi = false;
      });
    }
  };

  validateCurrentStep = () => {
    if (!this.navigation.current || !this.deliveryLimitations) return false;

    switch (this.navigation.current.name) {
      case 'parcel-details': {
        const isParcelWeightValid =
          this.parcelDimensions?.weight &&
          this.parcelDimensions.weight <= this.deliveryLimitations.maxWeight &&
          this.parcelDimensions.weight >= this.deliveryLimitations.minWeight;

        const isParcelSizesValid =
          !this.routeNotFoundError &&
          this.parcelDimensions?.size_x &&
          this.parcelDimensions?.size_y &&
          this.parcelDimensions?.size_z &&
          this.deliveryLimitations &&
          calculateVolumetricWeight({
            width: this.parcelDimensions.size_x,
            height: this.parcelDimensions.size_y,
            length: this.parcelDimensions.size_z,
            measure: store.userStore.measures,
          }) <= this.deliveryLimitations.maxVolWeight &&
          this.parcelDimensions.size_x <= this.deliveryLimitations.maxSide &&
          this.parcelDimensions.size_y <= this.deliveryLimitations.maxSide &&
          this.parcelDimensions.size_z <= this.deliveryLimitations.maxSide &&
          this.parcelDimensions.size_x >= 0.1 &&
          this.parcelDimensions.size_y >= 0.1 &&
          this.parcelDimensions.size_z >= 0.1;

        const isParcelDimensionsValid = !this.isDocumentShipmentType
          ? isParcelWeightValid && isParcelSizesValid
          : true;

        const isCountriesValid =
          this.countryDeparture &&
          this.countryDestination &&
          this.countryDeparture.zipCode &&
          (getSettingReceiverCountry(
            this.countryDeparture?.code,
            this.isDomestic
          ) === 'without_zip' ||
            this.countryDestination.zipCode) &&
          this.countryDeparture.code &&
          this.countryDestination.code;

        let isInsuranceStep0Valid = false;

        if (
          this.showStep0Insurance &&
          this.insuranceLimits?.min_insurance !== undefined &&
          this.insuranceLimits?.max_insurance !== undefined
        ) {
          isInsuranceStep0Valid =
            !!this.domesticInsurance &&
            this.domesticInsurance >= this.insuranceLimits.min_insurance &&
            this.domesticInsurance <= this.insuranceLimits.max_insurance;
        }

        return !!(
          isParcelDimensionsValid &&
          isCountriesValid &&
          !this.isDeliveryOptionsFailed &&
          !this.isRateSchemaChanged &&
          (!this.showStep0Insurance || isInsuranceStep0Valid)
        );
      }
      case 'delivery-option': {
        const isMileSelectionValid =
          this.selectedDeliveryOption &&
          this.selectedDeliveryOption.selectedLastMile;
        return !!isMileSelectionValid;
      }
      case 'sender-details-pudo':
      case 'sender-details-concierge':
      case 'sender-details-courier': {
        const contactDetails = {
          phone: this.shipment?.sender?.phone?.phone_number,
          email: this.shipment?.sender?.email,
        };

        const isSenderContactValid =
          this.getContactDetailsValidationSchema('sender').isValidSync(
            contactDetails
          );

        const isPudoValid = !!this.selectedSenderPudo;
        const isCourierValid = getCourierAddressValidationSchema(
          UserRole.sender,
          this.shipment?.sender?.country,
          undefined,
          !!this.navigation.current.elements?.find(
            (element) => element.name === 'administrative-unit'
          )?.behavior?.allowed_values?.length
        ).isValidSync({
          country: this.shipment?.sender?.address?.country,
          region: this.shipment?.sender?.address?.region,
          city: this.shipment?.sender?.address?.city,
          post_code: this.shipment?.sender?.address?.post_code,
          street: this.shipment?.sender?.address?.street,
          building: this.shipment?.sender?.address?.building,
          section: this.shipment?.sender?.address?.section,
          apartment: this.shipment?.sender?.address?.apartment,
          note: this.shipment?.sender?.address?.note,
          buzz_code: this.shipment?.sender?.address?.buzz_code,
        });

        const isPudoDelivery =
          this.navigation.current.name === 'sender-details-pudo';

        const senderDataValid = isPudoDelivery
          ? isPudoValid && isCourierValid // check also courier address because of home address
          : isCourierValid;

        return !!(
          isSenderContactValid &&
          this.isSenderPersonalDataValid &&
          senderDataValid
        );
      }
      case 'receiver-details-pudo':
      case 'receiver-details-courier': {
        const isContactDetailsValid = this.getContactDetailsValidationSchema(
          'receiver'
        ).isValidSync({
          phone: this.shipment?.recipient?.phone?.phone_number,
          email: this.shipment?.recipient?.email,
        });

        const isPudoValid = !!this.selectedReceiverPudo;
        const isCourierValid = getCourierAddressValidationSchema(
          UserRole.receiver,
          this.shipment?.recipient?.country,
          undefined,
          !!this.navigation.current.elements?.find(
            (element) => element.name === 'administrative-unit'
          )?.behavior?.allowed_values?.length
        ).isValidSync({
          country: this.shipment?.recipient?.address?.country,
          region: this.shipment?.recipient?.address?.region,
          city: this.shipment?.recipient?.address?.city,
          post_code: this.shipment?.recipient?.address?.post_code,
          street: this.shipment?.recipient?.address?.street,
          building: this.shipment?.recipient?.address?.building,
          section: this.shipment?.recipient?.address?.section,
          apartment: this.shipment?.recipient?.address?.apartment,
          note: this.shipment?.recipient?.address?.note,
          buzz_code: this.shipment?.recipient?.address?.buzz_code,
        });

        return !!(
          isContactDetailsValid &&
          this.isReceiverPersonalDataValid &&
          (isPudoValid || isCourierValid)
        );
      }
      case 'parcel-content': {
        return !!(this.shipment?.items?.length ?? -1 > 0);
      }
      case 'summary': {
        return this.summaryFormIsValid;
      }
      case 'signature': {
        return !!this.validateSignature();
      }
      case 'verify-phone': {
        const isVerificationCodeValid =
          this.phoneVerificationCode && this.phoneVerificationCode.length === 4;

        return !!isVerificationCodeValid;
      }
      default:
        return true;
    }
  };

  validateSignature = () =>
    this.isSignatureRequired ? !!this.signature : true;

  setShipmentType = (type: string) => {
    this.shipmentType = type;
    this.resetShipmentDataInCaseDeliveryDetailsChanged();
  };

  getDeliveryLimitations = async () => {
    const receiverCountrySetting = getSettingReceiverCountry(
      this.countryDeparture?.code,
      this.isDomestic
    );

    // In cases when delivery-limitations request consumes a lot of time
    // we need to somehow show the user why 'Next step' button is disabled.
    // (reminder: isNextStepButtonLoading controls spinner on 'Next step' button)
    this.isNextStepButtonLoading = true;

    runInAction(() => {
      this.routeNotFoundError = undefined;
    });

    const getLimitations =
      store.commonStore.isGuestParcelCreation || !store.userStore.user
        ? agent.Guest.guestDeliveryLimitations
        : agent.Shipment.deliveryLimitations;

    const isDestinationZipRequired = receiverCountrySetting !== 'without_zip';

    if (
      !this.countryDestination ||
      !this.countryDeparture ||
      !this.countryDeparture?.zipCode ||
      (isDestinationZipRequired && !this.countryDestination?.zipCode)
    ) {
      this.isNextStepButtonLoading = false;
      return;
    }

    try {
      const limitations = await getLimitations({
        shipment_type: this.shipmentType ?? '',
        destination_country: this.countryDestination.code,
        destination_zip: this.countryDestination.zipCode,
        departure_country: this.countryDeparture.code,
        departure_zip: this.countryDeparture.zipCode,
      });

      runInAction(() => {
        this.deliveryLimitations = limitations;
        if (!limitations.nonStandardPackaging.enabled) {
          this.nonStandardPackaging = false;
        }
        this.routeNotFoundError = undefined;
      });
    } catch (error) {
      runInAction(() => {
        this.deliveryLimitations = undefined;
        if (isAxiosError<ApiResponse<RouteNotFoundError>>(error)) {
          this.routeNotFoundError = error.response?.data.data;
          return;
        }
        handleError(error);
      });
    } finally {
      runInAction(() => {
        this.isNextStepButtonLoading = false;
      });
    }
  };

  setIsLoadingShipmentApi = (value: boolean) => {
    this.isLoadingShipmentApi = value;
  };

  setDepartureShipmentCountry = (country: CountryDetails | null) => {
    this.countryDeparture = country;
  };

  setDestinationShipmentCountry = (country: CountryDetails | null) => {
    this.countryDestination = country;
  };

  setParcelDimensions = (dimensions: ParcelDimensions | null) => {
    this.parcelDimensions = dimensions;
  };

  setNonStandardPackaging = (checked: boolean) => {
    this.nonStandardPackaging = checked;
  };

  setDeliveryOption = (deliveryOption?: DeliveryOption) => {
    this.selectedDeliveryOption = deliveryOption;

    this.resetAfterSelectedOptionModified();
  };

  resetAfterSelectedOptionModified = () => {
    this.selectedSenderPudo = undefined;
    this.selectedReceiverPudo = undefined;
    this.removeStepFromSaved('delivery-option');
    this.resetNavigationAfterMileChange();
    this.clearDateInfo(); // Clear cached dateInfo when the first mile changes
  };

  setSelectedFirstMile = (
    deliveryOption: DeliveryOption,
    firstMile: FirstMile,
    isOptionSelect: boolean
  ) => {
    if (compareMiles(deliveryOption.selectedFirstMile, firstMile)) return;

    deliveryOption.selectedFirstMile = firstMile;

    if (isOptionSelect) {
      this.resetAfterSelectedOptionModified();
    }
  };

  setSelectedLastMile = (
    deliveryOption: DeliveryOption,
    lastMile: LastMile,
    isOptionSelect: boolean
  ) => {
    if (compareMiles(deliveryOption.selectedLastMile, lastMile)) return;

    deliveryOption.selectedLastMile = lastMile;
    if (isOptionSelect) {
      this.resetAfterSelectedOptionModified();
    }
  };

  setMiddleMileFilter = (filter: DeliveryMethod | undefined) => {
    this.selectedMiddleMileFilter = filter;
  };

  markAiBetaPanelPresented = () => {
    this.hasPresentedAiBetaPanel = true;
  };

  resetAllData = () => {
    runInAction(() => {
      this.isParcelCreationInProgress = false;
      this.hasPresentedAiBetaPanel = false;
      this.isLoadingShipmentApi = false;
      this.navigation = { ...defaultNavigation };
      this.savedSteps = [];
      this.countryDeparture = null;
      this.countryDestination = null;
      this.parcelDimensions = null;
      this.nonStandardPackaging = false;
      this.deliveryOptions = undefined;
      this.staticDeliveryTimeStrings = undefined;
      this.groupedDeliveryPrices = undefined;
      this.selectedDeliveryOption = undefined;
      this.selectedMiddleMileFilter = undefined;
      this.deliveryOptionHints = undefined;
      this.selectedSenderPudo = undefined;
      this.selectedReceiverPudo = undefined;
      this.shipment = undefined;
      this.foundPudoItems = undefined;
      this.foundPudoMeta = undefined;
      this.pudoFilterTimeWeight = undefined;
      this.pudoFilterGeocode = undefined;
      this.signature = undefined;
      this.insuranceLimits = undefined;
      this.isPhoneCodeValid = true;
      this.isPhoneVerified = false;
      this.phoneVerificationCode = undefined;
      this.deliveryLimitations = undefined;
      this.isDraftEditing = false;
      this.domesticInsurance = undefined;
      this.isDomestic = false;
      this.currentLocation = undefined;
      this.clearDateInfo();
      this.summaryConfirmFormValues = undefined;
      this.summaryFormIsValid = false;
      this.isNextStepButtonLoading = false;
      this.isRouteExpectationLoading = false;
      this.isNextButtonClicked = { value: false };
      this.isSignButtonClicked = false;
      this.omitLimitationsRequest = false;
      this.senderAddressInvalid = false;
      this.isSenderPersonalDataValid = false;
      this.isReceiverPersonalDataValid = false;
      this.recipientPersonalDataFormValues = undefined;
      this.senderPersonalDataFormValues = undefined;
      this.routeNotFoundError = undefined;
      this.isRateSchemaChanged = false;
      this.shipmentType = undefined;
      // this.taxesFee  = 0; // currently hardcoded

      clearPersistedStore(this);
    });
  };

  resetShipmentDataInCaseDeliveryDetailsChanged = () => {
    runInAction(() => {
      this.selectedDeliveryOption = undefined;
      this.selectedMiddleMileFilter = undefined;
      this.deliveryOptionHints = undefined;
      this.selectedSenderPudo = undefined;
      this.selectedReceiverPudo = undefined;
      this.isDeliveryOptionsFailed = false;
      this.clearDateInfo();
      this.savedSteps = [];
      this.resetNavigationAfterMileChange();
      this.isNextButtonClicked = { value: false };
      this.isSignButtonClicked = false;
      this.isSenderPersonalDataValid = false;
      this.isReceiverPersonalDataValid = false;
      this.recipientPersonalDataFormValues = undefined;
      this.senderPersonalDataFormValues = undefined;
      this.routeNotFoundError = undefined;
      this.isRateSchemaChanged = false;

      // Clear city for US case
      if (
        this.countryDeparture?.code === DEFAULT_COUNTRY.country_code &&
        this.countryDestination
      ) {
        this.countryDestination.city = undefined;
        this.countryDestination.region = undefined;
      }
    });
  };

  findShipmentCountry = (
    countryCode?: string,
    zipCode?: string,
    city?: string,
    departureCountryCode?: string
  ): CountryDetails | null => {
    // const countries = !departureCountryCode
    //   ? store.shipmentCountriesStore.senderCountries
    //   : this.shipmentCountries?.find(
    //       (c) => c.countryCode === departureCountryCode
    //     )?.destinationCountries;

    const localizationCountry =
      store.shipmentCountriesStore.senderCountries?.find(
        (c) => c.code === countryCode
      );

    if (!localizationCountry) return null;

    return {
      ...localizationCountry,
      zipCode,
      city,
    };
  };

  setIsSenderPersonalDataValid = (value: boolean) => {
    this.isSenderPersonalDataValid = value;
  };

  setIsReceiverPersonalDataValid = (value: boolean) => {
    this.isReceiverPersonalDataValid = value;
  };

  setIsDraftEditing = (isDraftEditing: boolean) => {
    this.isDraftEditing = isDraftEditing;
  };

  setDomesticInsurance = (amount: number | undefined) => {
    this.domesticInsurance = amount;
  };

  setIsDomestic = (isDomestic: boolean) => {
    this.isDomestic = isDomestic;
  };

  setPrefillInProgress = (value: boolean) => {
    this.prefillInProgress = value;
  };

  prefillDraftParcelData = async (lastDraftStep: StepName | undefined) => {
    if (!this.shipment) return;

    await store.shipmentCountriesStore.loadDirectionsList();

    const departureCountry = this.findShipmentCountry(
      this.shipment.sender?.country,
      this.shipment.sender?.address?.post_code,
      this.shipment.sender?.address?.city
    );

    this.setDepartureShipmentCountry(departureCountry);

    const destinationCountry = this.findShipmentCountry(
      this.shipment.recipient?.country,
      this.shipment.recipient?.address?.post_code,
      this.shipment.recipient?.address?.city,
      this.shipment.sender?.country
    );

    this.setDestinationShipmentCountry(destinationCountry);
    const parcelDimensions: ParcelDimensions = {
      size_x: this.shipment.parcel?.size_x ?? 0,
      size_y: this.shipment.parcel?.size_y ?? 0,
      size_z: this.shipment.parcel?.size_z ?? 0,
      weight: this.shipment.parcel?.weight ?? 0,
    };

    this.setParcelDimensions(parcelDimensions);

    this.setShipmentType(this.shipment.shipment_type);

    await this.getDeliveryLimitations();

    const selectedDeliveryOptions = await this.fetchSelectedDeliveryOptions(
      this.shipment.id
    );

    this.setNonStandardPackaging(
      selectedDeliveryOptions?.additional_options?.non_standard_packaging ??
        false
    );

    await this.obtainDeliveryOptionsV2();

    if (selectedDeliveryOptions) {
      const {
        first_mile: selectedFirstMile,
        middle_mile: middleMile,
        last_mile: selectedLastMile,
      } = selectedDeliveryOptions;

      const selectedOption = this.deliveryOptions?.find(
        (option) =>
          compareMiles(option.middle_mile, middleMile) &&
          option.first_miles.some((x) => compareMiles(x, selectedFirstMile)) &&
          option.last_miles.some((x) => compareMiles(x, selectedLastMile))
      );

      if (selectedOption) {
        selectedOption.selectedFirstMile = selectedFirstMile;
        selectedOption.selectedLastMile = selectedLastMile;

        this.setMiddleMileFilter(middleMile.delivery_method);
        this.setDeliveryOption(selectedOption);

        // Adaptation for domestic flow (rare case)
        if (
          selectedOption.middle_mile.delivery_method.toString() === 'undefined'
        ) {
          selectedOption.middle_mile.delivery_method = 'car';
        }
        this.setMiddleMileFilter(selectedOption.middle_mile.delivery_method);
        this.setDeliveryOption(selectedOption);
      }
    }

    if (this.shipment.sender?.carrier.pudo_info)
      this.setSenderPudo(this.shipment.sender.carrier.pudo_info);

    if (this.shipment.recipient?.carrier.pudo_info)
      this.setReceiverPudo(this.shipment.recipient.carrier.pudo_info);

    if (this.showStep0Insurance) {
      this.setDomesticInsurance(this.shipment.insurance);
    }

    runInAction(() => {
      this.prefillInProgress = false;
    });

    if (lastDraftStep) {
      const draftLastStepIndex = this.navigation.map.findIndex(
        (x) => x.name === lastDraftStep
      );
      // ignore 0 step cause we need to obtain delivery options
      for (let index = 0; index <= draftLastStepIndex; index++) {
        this.markStepAsSaved(this.navigation.map[index].name);
      }
    }
  };

  fetchSignature = async () => {
    this.isNextStepButtonLoading = true;
    if (!this.shipment?.id) return;

    try {
      const signatureBlob = await agent.Shipment.fetchSignature(
        this.shipment.id
      );
      if (signatureBlob) {
        const signatureDataUrl = URL.createObjectURL(signatureBlob);

        this.setSignature(signatureDataUrl);
      }
    } catch (error) {
      if (
        error instanceof AxiosError &&
        error.response?.status === ApiResponseStatus.NOT_FOUND
      ) {
        return;
      }

      handleError(error);
    } finally {
      runInAction(() => {
        this.isNextStepButtonLoading = false;
      });
    }
  };

  createParcel = async () => {
    if (!this.shipment?.id) return;

    try {
      const { clientId, sessionId } = getGAClientAndSession();
      const submitRequest: SubmitParcelRequest = {
        ...this.summaryConfirmFormValues,
        generate_payment_link: true,
        client_id: clientId,
        session_id: sessionId,
      };

      const createdParcel = await agent.Shipment.submitParcel(
        this.shipment.id,
        submitRequest
      );

      store.commonStore.analyticsManager.track('summary', {
        ecommerce: { items: [prepareEcommerceItem()] },
      });

      runInAction(() => {
        this.createdParcelPaymentLink = createdParcel?.payment_link;
      });

      return true;
    } catch (error) {
      if (checkRescheduleRequired(error)) {
        handleReschedule(this.shipment);
        return;
      }
      handleError(error);
    }
  };

  getPaymentLink = () => this.createdParcelPaymentLink;

  proceedToNextStep = async (navigate?: NavigateFunction) => {
    const ignoreSavedStateForSteps: StepName[] = [
      'sender-details-pudo',
      'sender-details-concierge',
      'sender-details-courier',
      'receiver-details-pudo',
      'receiver-details-courier',
      'parcel-content',
      'summary',
      'verify-phone',
    ];

    const { current } = this.navigation;

    if (!current) return;

    this.setIsNextButtonClicked(false);

    // For steps 3, 4, 5 (sender/receiver details, summary) we always store/request data from backend (a candidate for future optimization!).
    // Exception is guest parcel creation where call to step 3 creates new token

    // TODO: implement for all steps
    let isNextStepSuccess: boolean = false;

    const skipApiProcessingForStep =
      !ignoreSavedStateForSteps.includes(current.name) &&
      this.isStepSaved(current.name);

    if (!skipApiProcessingForStep) {
      switch (current.name) {
        case 'parcel-details':
          isNextStepSuccess = !!(await this.obtainDeliveryOptionsV2());
          if (isNextStepSuccess) {
            store.commonStore.analyticsManager.track('parcel_details', {
              ecommerce: { items: [prepareEcommerceItem()] },
            });
          }
          break;
        case 'delivery-option':
          isNextStepSuccess = this.isAgentFlowShowMap
            ? await this.prepareAgentShowMapFlow()
            : await this.storeDeliveryOptions();
          if (isNextStepSuccess) {
            store.commonStore.analyticsManager.track('delivery_options', {
              ecommerce: { items: [prepareEcommerceItem()] },
            });
          }
          break;
        case 'sender-details-concierge':
        case 'sender-details-pudo':
        case 'sender-details-courier':
          // TOOD: add remove step from saved in case of failure

          isNextStepSuccess = await this.submitSenderDetails();
          if (isNextStepSuccess) {
            store.commonStore.analyticsManager.track('sender_details', {
              ecommerce: { items: [prepareEcommerceItem()] },
            });
          }
          break;
        case 'receiver-details-pudo':
        case 'receiver-details-courier':
          isNextStepSuccess = await this.submitReceiverDetails();
          if (isNextStepSuccess) {
            store.commonStore.analyticsManager.track('receiver_details', {
              ecommerce: { items: [prepareEcommerceItem()] },
            });
          }
          break;
        case 'parcel-content':
          isNextStepSuccess = await this.finalizeSummaryStep();
          if (isNextStepSuccess) {
            store.commonStore.analyticsManager.track('parcel_content', {
              ecommerce: { items: [prepareEcommerceItem()] },
            });
          }
          break;
        case 'verify-phone':
          isNextStepSuccess = await this.finalizePhoneVerification();
          if (isNextStepSuccess) {
            store.commonStore.analyticsManager.track('verify_phone');
          }
          break;
        case 'summary':
          isNextStepSuccess = true;
          break;
        default:
          break;
      }

      this.markStepAsSaved(current.name);
    }

    if (this.isRateSchemaChanged) return;
    if (this.noDeliveryOptionAvailable) return;
    if (this.senderAddressInvalid) {
      this.setSenderAddressInvalid(false);
      return;
    }

    if (!isNextStepSuccess && !skipApiProcessingForStep) return;

    const stepToSkip = this.canSkipVerifyPhone ? 'verify-phone' : undefined;
    this.navigation = movePointerForward(this.navigation, stepToSkip);

    if (navigate && this.navigation.current) {
      this.navigateToStep(navigate, this.navigation.current);
    }
  };

  navigateToStep = (navigate: NavigateFunction, step: Step) => {
    const nextRoute = `${store.localizationsStore.localeParam}/${AppRoute.PORTAL}/${AppRoute.INTERNATIONAL}/${AppRoute.PARCEL_CREATE}/${step.name}`;
    navigate(nextRoute);
    window.scrollTo(0, 0);
  };

  proceedToPrevStep = (navigate?: NavigateFunction) => {
    const { current } = this.navigation;
    const preserveData = this.isStepSaved(current!.name);
    const stepToSkip = this.canSkipVerifyPhone ? 'verify-phone' : undefined;
    this.navigation = movePointerBack(this.navigation, stepToSkip);
    this.setSenderAddressInvalid(false);

    if (navigate && this.navigation.current) {
      this.navigateToStep(navigate, this.navigation.current);
    }

    this.setIsNextButtonClicked(false);

    switch (current!.name) {
      case 'delivery-option':
        runInAction(() => {
          if (!preserveData && !isPersisting(this)) {
            this.setDeliveryOption();
          }
        });
        break;
      default:
        break;
    }
  };

  obtainDeliveryOptionsV2 = async () => {
    if (!this.countryDeparture || !this.countryDestination) return;

    this.isNextStepButtonLoading = true;

    const dimensions = this.isDocumentShipmentType
      ? {
          weight: this.deliveryLimitations?.maxWeight ?? 0,
          size_x: this.deliveryLimitations?.maxX ?? 0,
          size_y: this.deliveryLimitations?.maxY ?? 0,
          size_z: this.deliveryLimitations?.maxZ ?? 0,
        }
      : {
          weight: this.parcelDimensions!.weight!,
          size_x: this.parcelDimensions!.size_x,
          size_y: this.parcelDimensions!.size_y,
          size_z: this.parcelDimensions!.size_z,
        };

    try {
      const request: DeliveryOptionsRequest = {
        departure_country: this.countryDeparture!.code,
        destination_country: this.countryDestination!.code,
        departure_zip: this.countryDeparture!.zipCode!, // '28068',
        destination_zip: this.countryDestination!.zipCode!,
        ...dimensions,
        shipment_id: this.shipment?.id,
        insurance: this.isDomestic ? this.domesticInsurance : undefined,
        shipment_type: this.shipmentType,
        non_standard_packaging:
          !this.isDocumentShipmentType &&
          !!this.deliveryLimitations?.nonStandardPackaging?.enabled &&
          this.nonStandardPackaging,
      };

      const { data: deliveryOptionsData } = store.commonStore
        .isGuestParcelCreation
        ? await agent.Guest.guestDeliveryOptionsV2(request)
        : await agent.Shipment.deliveryOptionsV2(request);

      await this.handleDeliveryOptionsResponse(deliveryOptionsData);

      const data = store.commonStore.isGuestParcelCreation
        ? await agent.Guest.guestDeliveryHints(
            this.countryDeparture.code,
            this.countryDestination.code
          )
        : await agent.Shipment.deliveryHints(
            this.countryDeparture.code,
            this.countryDestination.code
          );

      runInAction(() => {
        this.deliveryOptionHints = data;
      });

      if (this.isRateSchemaChanged) {
        // TODO: double check which error message to show in this case!
        throw new Error(t('delivery_options_not_found'));
      }
      return true;
    } catch (error) {
      this.isDeliveryOptionsFailed = true;
      handleError(error);
    } finally {
      runInAction(() => {
        this.isNextStepButtonLoading = false;
      });
    }
  };

  storeDeliveryOptions = async () => {
    this.isNextStepButtonLoading = true;

    const destinationWithoutZip =
      getSettingReceiverCountry(
        this.countryDeparture?.code,
        this.isDomestic
      ) === 'without_zip' && !this.countryDestination?.zipCode;

    if (
      !this.selectedDeliveryOption ||
      !this.selectedDeliveryOption?.selectedFirstMile ||
      !this.selectedDeliveryOption?.selectedLastMile
    )
      return false;

    try {
      const dimensions = this.isDocumentShipmentType
        ? {
            weight: this.deliveryLimitations?.maxWeight ?? 0,
            size_x: this.deliveryLimitations?.maxX ?? 0,
            size_y: this.deliveryLimitations?.maxY ?? 0,
            size_z: this.deliveryLimitations?.maxZ ?? 0,
          }
        : {
            weight: this.parcelDimensions!.weight!,
            size_x: this.parcelDimensions!.size_x,
            size_y: this.parcelDimensions!.size_y,
            size_z: this.parcelDimensions!.size_z,
          };

      const deliveryOptions: StoreDeliveryOptionsRequest = {
        shipment_id:
          this.shipment?.id && this.shipment.id > 0
            ? this.shipment.id.toString()
            : undefined,
        departure_carrier:
          this.selectedDeliveryOption.selectedFirstMile.carrier.slug!,
        departure_service: {
          service_type: this.selectedDeliveryOption.selectedFirstMile.service
            .code as ServiceType,
          delivery_service:
            this.selectedDeliveryOption.selectedFirstMile.delivery_service
              ?.slug,
        },
        destination_carrier:
          this.selectedDeliveryOption.selectedLastMile?.carrier.slug!,
        destination_service: {
          service_type: this.selectedDeliveryOption.selectedLastMile?.service
            .code as ServiceType,
        },
        middle_mile_id: this.selectedDeliveryOption.middle_mile.id,
        rate_schema_id:
          this.selectedDeliveryOption.selectedFirstMile.rate_schema_id,
        departure_country: this.countryDeparture!.code,
        departure_region: this.countryDeparture!.region!,
        departure_city: this.countryDeparture!.city!,
        departure_zip: this.countryDeparture!.zipCode!,
        destination_country: this.countryDestination!.code,
        destination_region: this.countryDestination!.region!,
        destination_city: this.countryDestination!.city!,
        destination_zip: this.countryDestination!.zipCode!,
        ...dimensions,
        insurance: this.domesticInsurance,
        shipment_type: this.shipmentType,
        non_standard_packaging:
          !this.isDocumentShipmentType &&
          !!this.deliveryLimitations?.nonStandardPackaging?.enabled &&
          this.nonStandardPackaging,
      };

      let shipment: Shipment | undefined;

      // Call non-guest api also in case guest user navigated back and changed first/last mile
      if (
        !store.commonStore.isGuestParcelCreation ||
        store.commonStore.guestAccessToken
      ) {
        shipment = await agent.Shipment.storeDeliveryOptions(deliveryOptions);
      } else {
        const res =
          await agent.Guest.guestStoreDeliveryOptions(deliveryOptions);

        shipment = res.shipment;

        // User is then obtained in auth-effects.tsx
        store.commonStore.setGuestAccessToken(res.tokens?.token ?? null);
        store.commonStore.setGuestRefreshToken(
          res.tokens?.refresh_token ?? null
        );
      }

      if (!shipment) {
        throw new Error("Info about shipment wasn't obtained from the API!");
      }

      if (destinationWithoutZip && this.countryDestination) {
        if (!shipment.recipient?.address?.post_code) {
          throw new Error("Destination zip-code wasn't obtained from the API!");
        }

        this.countryDestination.zipCode =
          shipment.recipient?.address?.post_code;

        if (this.countryDestination.zipCode) {
          const city = await getCityByZipCode(
            this.countryDestination.zipCode,
            this.countryDestination.code
          );
          this.countryDestination.city = city;
        }
      }
      await this.updateShipmentAndNavigation(shipment!.id, true);
      return true;
    } catch (error) {
      handleError(error);
      return false;
    } finally {
      runInAction(() => {
        this.isNextStepButtonLoading = false;
      });
    }
  };

  submitSenderDetails = async () => {
    this.isNextStepButtonLoading = true;

    const {
      personalDataFields,
      contactInfoFields,
      addressFields,
      courierPickupFields,
    } = this.prepareSenderDetails();

    if (addressFields.post_code && this.countryDeparture) {
      this.countryDeparture.zipCode = addressFields.post_code;
    }

    let response: ApiResponse<ShipmentResponseData>;

    try {
      const isPudoDelivery =
        this.navigation.current?.name === 'sender-details-pudo';

      if (isPudoDelivery && this.selectedSenderPudo) {
        const pudoFields: PudoFields = {
          branch_uuid: this.selectedSenderPudo.id,
          delivery_carrier:
            this.selectedDeliveryOption?.selectedFirstMile?.carrier.slug ?? '',
        };

        const storePudoRequest: StorePudoRecipientRequest = {
          shipment_id: this.shipment!.id,
          ...personalDataFields,
          ...contactInfoFields,
          ...pudoFields,
          ...addressFields,
        };

        response = await agent.Shipment.senderPudo(
          storePudoRequest,
          this.shipment!.id,
          this.navigation.current!.name
        );
      } else {
        // in case courier or concierge steps
        const storeCourierRequest: StoreCourierRecipientRequest = {
          shipment_id: this.shipment!.id,
          ...personalDataFields,
          ...contactInfoFields,
          ...addressFields,
          ...courierPickupFields,
        };

        response = await agent.Shipment.senderCourier(
          storeCourierRequest,
          this.shipment!.id,
          this.navigation.current!.name
        );
      }

      runInAction(() => {
        this.shipment = response.data?.shipment;
        if (response.data?.navigation) {
          this.updateNavigation(response.data?.navigation, true);
        }
      });

      return true;
    } catch (error) {
      if (this.shipment && checkRescheduleRequired(error)) {
        const handlePickupChoose = (slot: CourierPickupSlot) => {
          this?.setCourierPickupValues?.(slot);
        };
        handleReschedule(
          this.shipment,
          handlePickupChoose,
          translate('create_parcel_reschedule_pickup')
        );
        return false;
      }

      await handleError(error);
      this.handleUnprocessableAddress(error);
      return false;
    } finally {
      runInAction(() => {
        this.isNextStepButtonLoading = false;
      });
    }
  };

  private createStorePudoRequest = (
    type: 'recipient' | 'sender',
    shipment: Shipment,
    recipientPersonalDataFormValues: Record<string, string | null> | undefined,
    selectedPudo: PudoItem,
    selectedMile: LastMile
  ): StorePudoRecipientRequest => ({
    shipment_id: shipment.id,
    ...recipientPersonalDataFormValues,
    email: shipment[type]!.email,
    phone: shipment[type]!.phone!.phone_number ?? '',
    phone_country_code: shipment[type]!.phone!.country_code ?? '',
    branch_uuid: selectedPudo.id,
    delivery_carrier: selectedMile.carrier.slug!,
  });

  private createStoreCourierRequest = (
    shipment: Shipment,
    recipientPersonalDataFormValues: Record<string, string | null> | undefined
  ): StoreCourierRecipientRequest => {
    let request: StoreCourierRecipientRequest = {
      shipment_id: shipment.id,
      ...recipientPersonalDataFormValues,
      email: shipment.recipient!.email,
      phone: shipment.recipient!.phone!.phone_number ?? '',
      phone_country_code: shipment.recipient!.phone!.country_code ?? '',
      country: shipment.recipient!.country,
      post_code: shipment.recipient!.address!.post_code ?? '',
      region: shipment.recipient!.address!.region ?? '',
      city: shipment.recipient!.address!.city ?? '',
      street: shipment.recipient!.address!.street ?? '',
      building: shipment.recipient!.address!.building ?? '',
      section: shipment.recipient!.address!.section ?? '',
      apartment: shipment.recipient!.address!.apartment ?? '',
      buzz_code: shipment.recipient!.address!.buzz_code ?? '',
      note: shipment.recipient!.address!.note ?? '',
    };

    if (shipment?.recipient?.courier_pickup) {
      request = {
        ...request,
        courier_pickup: {
          time_to: shipment!.recipient!.courier_pickup?.time_to ?? '',
          date: shipment!.recipient!.courier_pickup?.date ?? '',
          timezone: shipment!.recipient!.courier_pickup?.timezone ?? '',
          time_from: shipment!.recipient!.courier_pickup?.time_from ?? '',
        },
      };
    }

    const withoutNulls = (obj: StoreCourierRecipientRequest) =>
      Object.fromEntries(
        Object.entries(obj).filter(([, v]) => v !== null)
      ) as StoreCourierRecipientRequest;

    return withoutNulls(request);
  };

  submitReceiverDetails = async () => {
    this.isNextStepButtonLoading = true;

    try {
      await this.handleReceiverDetails();
      return true;
    } catch (error) {
      await handleError(error);
      this.handleUnprocessableAddress(error);
      return false;
    } finally {
      runInAction(() => {
        this.isNextStepButtonLoading = false;
      });
    }
  };

  finalizePhoneVerification = async () => {
    await this.verifyPhone();
    this.setIsPhoneVerified(this.isPhoneCodeValid);
    return this.isPhoneCodeValid;
  };

  setIsVerifyPhoneCodeValid = (value: boolean) => {
    this.isPhoneCodeValid = value;
  };

  finalizeSummaryStep = async () => {
    this.isNextStepButtonLoading = true;

    try {
      await agent.Shipment.parcelContent(this.shipment!.id);

      await this.getShipmentDetails(this.shipment!.id);

      return true;
    } catch (error) {
      await handleError(error);
      return false;
    } finally {
      runInAction(() => {
        this.isNextStepButtonLoading = false;
      });
    }
  };

  private handleStorePudoRequest = async (
    storePudoRequest: StorePudoRecipientRequest
  ) => agent.Shipment.receiverPudo(storePudoRequest, this.shipment!.id);

  private handleStoreCourierRequest = async (
    storeCourierRequest: StoreCourierRecipientRequest
  ) => agent.Shipment.receiverCourier(storeCourierRequest, this.shipment!.id);

  setRecipientPersonalDataFormValues = (
    formValues: Record<string, string | null> | undefined
  ) => {
    this.recipientPersonalDataFormValues = formValues;
  };

  setSenderPersonalDataFormValues = (
    formValues: Record<string, string | null> | undefined
  ) => {
    this.senderPersonalDataFormValues = formValues;
  };

  updateShipmentAndNavigation = async (
    shipmentId: number,
    updateNavigationFromBackend: boolean = false
  ) => {
    const { navigation: newNavigation, shipment } =
      await agent.Shipment.shipmentSteps(shipmentId);
    runInAction(() => {
      this.shipment = shipment;
      if (updateNavigationFromBackend && newNavigation) {
        this.updateNavigation(newNavigation);
      }
    });
    return newNavigation;
  };

  updateNavigation = async (
    newNavigation: ParcelCreationNavigation,
    takeNextStepFromNewNavigation: boolean = false
  ) => {
    runInAction(() => {
      // We should merge the map obtained from backend (steps > 2) with default steps for seamless navigation.
      // Cur, prev and next pointers are preserved on purpose.
      // We do not rely on backend here and move the pointer manually when user navigates back and forth.

      let nextStep: Step | null | undefined;

      if (this.navigation.current?.name === 'delivery-option') {
        nextStep = newNavigation.map[0];
      } else {
        nextStep = takeNextStepFromNewNavigation
          ? newNavigation.current // take 'current' from new navigation instead of 'next', because backed already moved the pointer forward
          : this.navigation.next;
      }

      const verifyPhoneStepIndex = this.navigation.map.findIndex(
        (x) => x.name === 'verify-phone'
      );
      const verifyPhoneStepIndexNew = newNavigation.map.findIndex(
        (x) => x.name === 'verify-phone'
      );

      // In case old navigation already contained verify-phone we need to preserve it
      if (verifyPhoneStepIndex > 0 && verifyPhoneStepIndexNew < 0) {
        const verifyPhoneStep = this.navigation.map[verifyPhoneStepIndex];

        newNavigation.map.splice(
          verifyPhoneStepIndex - defaultNavigationMap.length,
          0,
          verifyPhoneStep
        );

        if (this.navigation.current?.name.startsWith('sender')) {
          nextStep = verifyPhoneStep;
        }
      }

      this.navigation = {
        ...this.navigation,
        next: nextStep,
        map: [...defaultNavigationMap, ...newNavigation.map],
      };
      if (this.isSignatureRequired) {
        this.navigation.map = [...this.navigation.map, signatureStep];
      }
    });
  };

  private handleReceiverDetails = async () => {
    if (this.selectedReceiverPudo) {
      const storePudoRequest = this.createStorePudoRequest(
        'recipient',
        this.shipment!,
        this.recipientPersonalDataFormValues,
        this.selectedReceiverPudo,
        this.selectedDeliveryOption?.selectedLastMile!
      );
      const response = await this.handleStorePudoRequest(storePudoRequest);

      runInAction(() => {
        this.shipment = response.data?.shipment;
        if (response.data?.navigation) {
          this.updateNavigation(response.data.navigation);
        }
        this.setRecipientPersonalDataFormValues(undefined);
      });
    } else if (this.shipment!.recipient?.address?.street) {
      const storeCourierRequest = this.createStoreCourierRequest(
        this.shipment!,
        this.recipientPersonalDataFormValues
      );

      if (storeCourierRequest.post_code && this.countryDestination) {
        this.countryDestination.zipCode = storeCourierRequest.post_code;
      }

      const response =
        await this.handleStoreCourierRequest(storeCourierRequest);

      runInAction(() => {
        this.shipment = response.data?.shipment;
        if (response.data?.navigation) {
          this.updateNavigation(response.data.navigation);
        }
        this.setRecipientPersonalDataFormValues(undefined);
      });
    }
  };

  setSenderPhone = (phone: string) => {
    if (!this.shipment?.sender) return;

    if (!this.shipment.sender.phone) {
      this.shipment!.sender!.phone = { phone_number: null, country_code: null };
    }
    if (
      this.isPhoneVerified &&
      this.shipment!.sender!.phone.phone_number !== phone
    ) {
      // In case phone was changed we need to reset isPhoneVerified flag
      this.setIsPhoneVerified(false);
      this.setPhoneVerificationCode(undefined);
    }
    this.shipment!.sender!.phone.phone_number = phone;
  };

  setSenderPhoneCountryCode = (countryCode: string) => {
    if (!this.shipment!.sender!.phone) {
      this.shipment!.sender!.phone = { phone_number: null, country_code: null };
    }
    this.shipment!.sender!.phone.country_code = countryCode;
  };

  setSenderEmail = (email: string) => {
    this.shipment!.sender!.email = email;
  };

  setSenderPudo = (pudo?: PudoItem) => {
    this.selectedSenderPudo = pudo;
  };

  verifyPhone = async () => {
    this.isNextStepButtonLoading = true;
    try {
      await agent.Shipment.verifyPhone(
        this.shipment!.sender!.phone!.country_code!,
        this.shipment!.sender!.phone!.phone_number!,
        this.phoneVerificationCode!
      );
      this.setIsVerifyPhoneCodeValid(true);
    } catch (error) {
      this.setIsVerifyPhoneCodeValid(false);
      handleError(error);
    } finally {
      runInAction(() => {
        this.isNextStepButtonLoading = false;
      });
    }
  };

  sendVerificationMessage = async (phone: Phone) => {
    this.isLoadingShipmentApi = true;
    try {
      await agent.Shipment.sendVerificationMessage(phone);

      return true;
    } catch (error) {
      handleError(error);
      return false;
    } finally {
      runInAction(() => {
        this.isLoadingShipmentApi = false;
      });
    }
  };

  setPhoneVerificationCode = (code: string | undefined) => {
    this.phoneVerificationCode = code;
  };

  setReceiverPhone = (phone: string) => {
    if (!this.shipment!.recipient!.phone) {
      this.shipment!.recipient!.phone = {
        phone_number: null,
        country_code: null,
      };
    }
    this.shipment!.recipient!.phone.phone_number = phone;
  };

  setReceiverPhoneCountryCode = (countryCode: string) => {
    if (!this.shipment!.recipient!.phone) {
      this.shipment!.recipient!.phone = {
        phone_number: null,
        country_code: null,
      };
    }
    this.shipment!.recipient!.phone.country_code = countryCode;
  };

  setReceiverEmail = (email: string) => {
    this.shipment!.recipient!.email = email;
  };

  setReceiverPudo = (pudo?: PudoItem) => {
    this.selectedReceiverPudo = pudo;
  };

  getPudosForRole = async ({ role, useCache }: GetPudosForRoleProps) => {
    if (useCache && this.foundPudoItems)
      return { pudos: this.foundPudoItems, pudosMeta: this.foundPudoMeta };

    const country =
      role === UserRole.sender
        ? this.countryDeparture
        : this.countryDestination;

    const carrier =
      role === UserRole.sender
        ? this.selectedDeliveryOption?.selectedFirstMile?.carrier.slug
        : this.selectedDeliveryOption?.selectedLastMile?.carrier.slug;

    if (!carrier || !country) return;

    this.isLoadingSearchApi = true;

    try {
      const pudoSearchRequest: PudoSearch = {
        shipment_id: this.shipment?.id?.toString(),
        country: country.code,
        postal_code: this.pudoFilterGeocode ? undefined : country.zipCode,
        mode: 'drop_off',
        search: this.pudoFilterSearch,
        filters: {
          weight: this.pudoFilterTimeWeight?.weight,
          open24_7: this.pudoFilterTimeWeight?.open24_7,
          open_on_weekends: this.pudoFilterTimeWeight?.open_on_weekends,
          open_now: this.pudoFilterTimeWeight?.open_now,
          timezone: getCurrentTimezoneOffset(),
        },
        distance: {
          distance: this.pudoFilterDistance ? this.pudoFilterDistance : 100,
          measurement: store.userStore.measures,
        },
        location: {
          lat: this.pudoFilterGeocode?.latitude,
          lon: this.pudoFilterGeocode?.longitude,
        },
      };

      if (this.isAgentFlowShowMap) {
        pudoSearchRequest.pudo_type = 'agent';
      }

      const searchPudo = store.commonStore.isGuestParcelCreation
        ? agent.Guest.guestSearchPudo
        : agent.Search.searchPudo;

      const response = await searchPudo(carrier, pudoSearchRequest);

      runInAction(() => {
        // Temp solution until backend will clear its DB from duplicates
        this.foundPudoItems = removeDuplicates(
          response.data,
          (pudo) => pudo.id
        );
        this.foundPudoMeta = response.meta;
      });

      return { pudos: this.foundPudoItems, pudosMeta: this.foundPudoMeta };
    } catch (error) {
      handleError(error);
    } finally {
      runInAction(() => {
        this.isLoadingSearchApi = false;
      });
    }
  };

  setFoundPudos = (pudos: PudoItem[] | undefined) => {
    this.foundPudoItems = pudos;
  };

  setFoundPudoMeta = (meta: PudoSearchMeta | undefined) => {
    this.foundPudoMeta = meta;
  };

  clearPudoFilters = () => {
    this.pudoFilterTimeWeight = undefined;
    this.pudoFilterDistance = undefined;
    this.pudoFilterGeocode = undefined;
    this.pudoFilterSearch = undefined;
  };

  setPudoFilterTimeWeight = (timeWeightFilter?: PudoFilters) => {
    this.pudoFilterTimeWeight = timeWeightFilter;
  };

  setPudoFilterDistance = (distance?: number) => {
    this.pudoFilterDistance = distance;
  };

  setPudoFilterGeocode = (geocode?: Geocode) => {
    this.pudoFilterGeocode = geocode;
  };

  setPudoFilterSearch = (searchString?: string) => {
    this.pudoFilterSearch = searchString;
  };

  setPreservePudos = (preserve: boolean) => {
    this.preservePudos = preserve;
  };

  setSenderAddress = (address: Address) => {
    if (this.shipment?.sender) {
      this.shipment.sender.address = address;
    }
  };

  setReceiverAddress = (address: Address) => {
    if (this.shipment?.recipient) {
      this.shipment.recipient.address = address;
    }
  };

  submitSignature = async (signature: FormData) => {
    if (!this.shipment?.id) return;

    try {
      const response = await agent.Shipment.submitSignature(
        signature,
        this.shipment.id
      );

      return response;
    } catch (error) {
      handleError(error);
    }
  };

  setSignature = (signature: string) => {
    runInAction(() => {
      this.signature = signature;
    });
  };

  setSummaryConfirmFormValues = (value: CheckboxValues) => {
    this.summaryConfirmFormValues = value;
  };

  setSummaryFormIsValid = (value: boolean) => {
    this.summaryFormIsValid = value;
  };

  setTaxesInfo = (taxesInfo: IGenericInfoBlock | undefined) => {
    this.taxesInfo = taxesInfo;
  };

  getTaxesInfo = async () => {
    if (!this.shipment?.id) return;
    this.isLoadingTaxes = true;

    let success = false;

    try {
      const res = await agent.Shipment.taxes(this.shipment.id);
      this.setTaxesInfo(res);
      success = true;
    } catch (error: any) {
      if (error.response.status === ApiResponseStatus.NOT_FOUND) {
        return false;
      }

      handleError(error);
      success = false;
    } finally {
      runInAction(() => {
        this.isLoadingTaxes = false;
      });
    }

    return success;
  };

  getProhibitedItems = async () => {
    if (!this.shipment?.id) return;

    this.isLoadingShipmentApi = true;

    try {
      const prohibitedItems = await agent.Shipment.prohibitedItems(
        this.shipment.id
      );

      return prohibitedItems;
    } catch (error) {
      handleError(error);
    } finally {
      runInAction(() => {
        this.isLoadingShipmentApi = false;
      });
    }
  };

  getNonCompensatedItems = async () => {
    if (!this.shipment?.id) return;

    this.isLoadingShipmentApi = true;

    try {
      const nonCompensatedItems = await agent.Shipment.nonCompensatedItems(
        this.shipment.id
      );

      return nonCompensatedItems;
    } catch (error) {
      handleError(error);
    } finally {
      runInAction(() => {
        this.isLoadingShipmentApi = false;
      });
    }
  };

  searchItemsByDescription = async (description?: string) => {
    if (!description || description?.trim().length < 3) return undefined;

    this.isLoadingSearchApi = true;

    try {
      const hsCodesSearchRequest: HscodeSearchRequestData = {
        description,
        lang: store.localizationsStore.selectedLocalization!.code.toLocaleLowerCase(),
        origin_country: this.countryDeparture?.code,
        destination_country: this.countryDestination?.code,
      };
      const foundItems = await agent.Search.hscode(hsCodesSearchRequest);

      return foundItems;
    } catch (error) {
      if (
        error instanceof AxiosError &&
        error.response?.status === ApiResponseStatus.BAD_REQUEST
      )
        return [];
      handleError(error);
    } finally {
      runInAction(() => {
        this.isLoadingSearchApi = false;
      });
    }
  };

  addItemToShipment = async (product: ParcelItem) => {
    if (!this.shipment) return;

    this.isLoadingShipmentApi = true;

    try {
      const shipment = await agent.Shipment.addParcelItem(
        new HsCodeRequest(product),
        this.shipment.id
      );
      runInAction(() => {
        this.shipment = shipment;
      });
    } catch (error) {
      const declaredValueError = tryGetErrorMessage(
        error,
        ApiResponseStatus.PRECONDITION_FAILED,
        'declared_value'
      );

      if (declaredValueError) return declaredValueError;
      handleError(error);
    } finally {
      runInAction(() => {
        this.isLoadingShipmentApi = false;
      });
    }
  };

  modifyItemInShipment = async (product: ParcelItem) => {
    if (!this.shipment) return;

    this.isLoadingShipmentApi = true;

    try {
      const shipment = await agent.Shipment.updateParcelItem(
        new HsCodeRequest(product),
        this.shipment.id
      );
      runInAction(() => {
        this.shipment = shipment;
      });
    } catch (error) {
      const declaredValueError = tryGetErrorMessage(
        error,
        ApiResponseStatus.PRECONDITION_FAILED,
        'declared_value'
      );

      if (declaredValueError) return declaredValueError;
      handleError(error);
    } finally {
      runInAction(() => {
        this.isLoadingShipmentApi = false;
      });
    }
  };

  deleteItemInShipment = async (productId: number) => {
    if (!this.shipment) return;

    this.isLoadingShipmentApi = true;

    try {
      const shipment = await agent.Shipment.deleteParcelItem(
        productId,
        this.shipment.id
      );

      runInAction(() => {
        this.shipment = shipment;
      });
    } catch (error) {
      handleError(error);
    } finally {
      runInAction(() => {
        this.isLoadingShipmentApi = false;
      });
    }
  };

  modifyShipmentInsurance = async (insurance: number) => {
    if (!this.shipment) return;

    this.isLoadingShipmentApi = true;

    try {
      const updatedShipment = await agent.Shipment.updateParcelInsurance(
        this.shipment.id,
        { insurance }
      );
      runInAction(() => {
        this.shipment = updatedShipment;
      });
    } catch (error) {
      handleError(error);
    } finally {
      runInAction(() => {
        this.isLoadingShipmentApi = false;
      });
    }
  };

  updateShipmentInsuranceCoverage = async (coverage: InsuranceCoverage) => {
    if (!this.shipment) return;

    this.isLoadingShipmentApi = true;

    try {
      const updatedShipment = await agent.Shipment.updateInsuranceCoverage(
        this.shipment.id,
        coverage
      );
      runInAction(() => {
        this.shipment = updatedShipment;
      });
    } catch (error) {
      handleError(error);
    } finally {
      runInAction(() => {
        this.isLoadingShipmentApi = false;
      });
    }
  };

  fetchInsuranceLimits = async () => {
    if (!this.shipment) return;

    this.isLoadingShipmentApi = true;

    try {
      const limits = await agent.Shipment.getParcelInsuranceLimits(
        this.shipment.id
      );
      runInAction(() => {
        this.insuranceLimits = limits;
      });
    } catch (error) {
      handleError(error);
    } finally {
      runInAction(() => {
        this.isLoadingShipmentApi = false;
      });
    }
  };

  fetchDomesticInsuranceLimits = async () => {
    if (!this.countryDeparture?.code || !this.countryDestination?.code) return;

    this.isLoadingShipmentApi = true;

    try {
      const limits = await agent.Shipment.getDomesticInsuranceLimits(
        this.countryDeparture.code,
        this.countryDestination.code,
        store.userStore.user?.currency
      );
      runInAction(() => {
        this.insuranceLimits = limits;
      });
    } catch (error) {
      handleError(error);
    } finally {
      runInAction(() => {
        this.isLoadingShipmentApi = false;
      });
    }
  };

  getReceiverDetails = async (receiverId: number) => {
    this.isNextStepButtonLoading = true;

    try {
      const res = await agent.Profile.getReceiver(receiverId);
      const receiverDetails = res.data ?? [];
      return receiverDetails;
    } catch (error) {
      handleError(error);
    } finally {
      runInAction(() => {
        this.isNextStepButtonLoading = false;
      });
    }
  };

  setCurrentLocation = (location: Geocode | undefined) => {
    this.currentLocation = location;
  };

  setIsPhoneVerified = (isVerified: boolean) => {
    this.isPhoneVerified = isVerified;
  };

  setIsParcelCreationInProgress = (inProgress: boolean) => {
    this.isParcelCreationInProgress = inProgress;
  };

  storePickupWindows = async (
    shipmentId: number,
    pickupSlot: CourierPickupSlot
  ) => {
    try {
      this.isLoadingApiStorePickupWindow = true;

      const res = await agent.Shipment.storePickupWindows(
        shipmentId,
        pickupSlot
      );

      return res?.link;
    } catch (error) {
      handleError(error);
    } finally {
      runInAction(() => {
        this.isLoadingApiStorePickupWindow = false;
      });
    }
  };

  fetchDates = async (shipmentId: number) => {
    try {
      this.isLoadingApiCallCourier = true;

      const response = await agent.Shipment.pickupWindows(
        shipmentId,
        getCurrentTimezoneOffset()
      );

      runInAction(() => {
        this.dateInfo = response?.dates as PickupDateTimeSlots[];
        this.cachedTimezone = getCurrentTimezoneOffset();
      });

      return this.dateInfo;
    } catch (error) {
      handleError(error);
    } finally {
      runInAction(() => {
        this.isLoadingApiCallCourier = false;
      });
    }
  };
  getAllDates = async (shipmentId: number, allowCache: boolean = true) => {
    const currentTimezone = getCurrentTimezoneOffset();

    // Check if we have a cached dateInfo and if it's still valid
    if (
      allowCache &&
      this.dateInfo &&
      this.shipment &&
      this.cachedTimezone === currentTimezone
    ) {
      return this.dateInfo;
    }

    return this.fetchDates(shipmentId);
  };

  setCourierPickupValues = (pickupInfo: CourierPickupSlot) => {
    const user = this.shipment?.sender;

    if (user) {
      user.courier_pickup = {
        ...user.courier_pickup,
        ...pickupInfo,
        timezone: getCurrentTimezoneOffset(),
      };
    }
  };

  clearDateInfo = () => {
    this.dateInfo = undefined;
    this.cachedTimezone = null;
  };

  applyPromoCode = async (code: string) => {
    try {
      if (!this.shipment) return;

      const shipmentId = this.shipment.id;

      const updatedShipment = await agent.Promocodes.promocodes(
        shipmentId,
        code
      );

      const appliedPromoCode = updatedShipment?.promocodes?.find(
        (promo) => promo.code.toLocaleLowerCase() === code.toLocaleLowerCase()
      );

      if (!appliedPromoCode) return;

      runInAction(() => {
        this.shipment = updatedShipment;
      });

      return {
        code: appliedPromoCode.code,
        discount: appliedPromoCode.discount,
      };
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.log('PROMOCODE', error);

      if (error.response?.status === ApiResponseStatus.SERVER_ERROR) {
        handleError(error);
        return;
      }

      throw new Error(error.response.data.message);
    }
  };

  handleRemovePromoCode = async (code: string) => {
    try {
      if (!this.shipment || !this.shipment.promocodes) return;

      const promoCodeToRemove = this.shipment.promocodes.find(
        (promo) => promo.code.toLocaleLowerCase() === code.toLocaleLowerCase()
      );

      if (!promoCodeToRemove) return;

      const updatedShipment = await agent.Promocodes.deletePromocode(
        this.shipment.id,
        promoCodeToRemove.id
      );

      runInAction(() => {
        this.shipment = updatedShipment;
      });
    } catch (error) {
      console.error(error);
    }
  };

  setCalculatorDataFromStore = () => {
    this.setDepartureShipmentCountry(
      store.widgetCalculatorStore.countryDeparture
    );
    this.setDestinationShipmentCountry(
      store.widgetCalculatorStore.countryDestination
    );
    this.setParcelDimensions(store.widgetCalculatorStore.parcelDimensions);
  };

  setCalculatorDataFromUrl = async ({
    countryDeparture: {
      countryCode: departureCountryCode,
      zipCode: departureZipCode,
    },
    countryDestination: {
      countryCode: destinationCountryCode,
      zipCode: destinationZipCode,
    },
    parcelDimensions,
    measures: urlMeasures,
  }: ICalculatorData) => {
    await store.shipmentCountriesStore.loadDirectionsList();

    const departureCountry: CountryDetails | null =
      store.shipmentCountriesStore.senderCountries.find(
        (country) => country.code === departureCountryCode
      ) || null;
    const destinationCountry: CountryDetails | null =
      store.shipmentCountriesStore.receiverCountries.find(
        (country) => country.code === destinationCountryCode
      ) || null;

    if (departureCountry) departureCountry.zipCode = departureZipCode;
    if (destinationCountry) destinationCountry.zipCode = destinationZipCode;

    await runInAction(async () => {
      this.setOmitLimitationsRequest(true);
      this.setDepartureShipmentCountry(departureCountry);
      this.setDestinationShipmentCountry(destinationCountry);
    });

    const conversionRequired =
      urlMeasures && urlMeasures !== store.userStore.measures;

    if (conversionRequired) {
      const convertedDimensions = convertDimensions(
        parcelDimensions,
        urlMeasures
      );
      this.setParcelDimensions(convertedDimensions);
    } else {
      this.setParcelDimensions(parcelDimensions);
    }

    if (store.widgetCalculatorStore.deliveryLimitations) {
      runInAction(() => {
        this.deliveryLimitations = _.cloneDeep(
          store.widgetCalculatorStore.deliveryLimitations
        );
      });
    } else {
      await this.getDeliveryLimitations();
    }
    store.widgetCalculatorStore.resetStore();
  };

  fetchSelectedDeliveryOptions = async (shipmentId: number) => {
    this.isNextStepButtonLoading = true;

    try {
      const deliveryOptions =
        await agent.Shipment.getSelectedDeliveryOptions(shipmentId);

      return deliveryOptions;
    } catch (error) {
      handleError(error);
    } finally {
      runInAction(() => {
        this.isNextStepButtonLoading = false;
      });
    }
  };

  handleMessages = (
    messageBus: MessageBus[] | undefined,
    responseShipment?: Shipment
  ) => {
    if (!messageBus?.length) return;

    const messageHandlers: BusMessageHandler[] = [
      {
        selector: ({ type, additional_information: additionalInformation }) =>
          type === 'reprice' &&
          !!responseShipment &&
          additionalInformation.some(
            (info) => info.key === 'difference' && info.value !== '0'
          ),
        handler: async () => {
          runInAction(() => {
            if (
              responseShipment?.sender?.address?.post_code &&
              this.countryDeparture
            ) {
              this.countryDeparture.zipCode =
                responseShipment.sender.address.post_code;
            }

            if (
              responseShipment?.recipient?.address?.post_code &&
              this.countryDestination
            ) {
              this.countryDestination.zipCode =
                responseShipment.recipient.address.post_code;
            }
          });

          if (responseShipment?.id) {
            store.commonStore.toastInfo(t('price_update_message'), true);
            await this.obtainDeliveryOptionsV2();

            const selectedDeliveryOptions =
              await this.fetchSelectedDeliveryOptions(responseShipment.id);

            if (!selectedDeliveryOptions || !this.selectedDeliveryOption)
              return;

            this.selectedDeliveryOption.selectedFirstMile =
              selectedDeliveryOptions.first_mile;
            this.selectedDeliveryOption.selectedLastMile =
              selectedDeliveryOptions.last_mile;

            this.setSenderPudo(responseShipment.sender?.carrier?.pudo_info);
            this.setReceiverPudo(
              responseShipment.recipient?.carrier?.pudo_info
            );
          }
        },
      },
      {
        selector: ({ type, additional_information: additionalInformation }) =>
          this.navigation.current?.name === 'parcel-details' &&
          type === 'reprice' &&
          additionalInformation.some(
            (info) => info.key === 'rate_schema_was_changed'
          ),
        handler: (msg) => {
          this.isRateSchemaChanged = msg.additional_information.some(
            (info) => info.key === 'rate_schema_was_changed'
          );
        },
      },
      {
        selector: (msg: MessageBus) =>
          msg.type === 'promoCode' && msg.subtype === 'promoCodeDeleted',
        handler: (msg: MessageBus) => {
          store.commonStore.toastError(msg.message);
        },
      },
      {
        selector: (msg: MessageBus) =>
          msg.type === 'messageNotification' &&
          msg.subtype === 'success' &&
          !store.commonStore.isGuestParcelCreation,
        handler: (msg: MessageBus) => {
          const toastMessage: ToastContent = {
            primaryMessage: msg.additional_information[0].value,
            secondaryMessage: msg.additional_information[1].value,
          };
          store.commonStore.toastSuccess(toastMessage);
          store.userStore.getCurrentUser();
        },
      },
      {
        selector: (msg: MessageBus) =>
          msg.type === 'messageNotification' && msg.subtype === 'warning',
        handler: (msg: MessageBus) => {
          const title = msg.additional_information.find(
            (info) => info.key === 'title'
          )?.value;
          const description = msg.additional_information.find(
            (info) => info.key === 'description'
          )?.value;

          if (!title) return;

          store.commonStore.toastAllert({
            primaryMessage: title,
            secondaryMessage: description,
          });
        },
      },
    ];

    handleBusMessages(messageBus, messageHandlers, true);
  };

  prepareAgentShowMapFlow = async () => {
    runInAction(() => {
      this.isNextStepButtonLoading = true;
    });

    const response = await this.getPudosForRole({ role: UserRole.sender });
    if (!response) return false;

    const { pudos } = response;

    const nearestPudo = pudos?.sort(
      (a, b) =>
        Number.parseFloat(a.distance.distance) -
        Number.parseFloat(b.distance.distance)
    )[0];

    if (nearestPudo) {
      this.setSenderPudo(nearestPudo);
    }

    runInAction(() => {
      this.navigation = {
        ...this.navigation,
        next: agentFlowDeliveryInstructions,
        map: [...defaultNavigationMap, agentFlowDeliveryInstructions],
      };
    });
    runInAction(() => {
      this.isNextStepButtonLoading = false;
    });

    return true;
  };

  resetNavigationAfterMileChange = () => {
    if (this.prefillInProgress) return;

    if (this.navigation.current?.name === 'parcel-details') {
      this.navigation = { ...defaultNavigation };
    }

    if (this.navigation.current?.name === 'delivery-option') {
      this.navigation = { ...defaultNavigation };
      this.navigation.current = defaultNavigation.map.at(1);
      this.navigation.previous = defaultNavigation.map.at(0);
      this.navigation.next = null;
    }
  };

  setIsNextStepButtonLoading = (isLoading: boolean) => {
    this.isNextStepButtonLoading = isLoading;
  };

  setIsNextButtonClicked = (value: boolean) => {
    this.isNextButtonClicked = { value };
  };

  setIsSignButtonClicked = (value: boolean) => {
    this.isSignButtonClicked = value;
  };

  setOmitLimitationsRequest = (value: boolean) => {
    this.omitLimitationsRequest = value;
  };

  registerRouteExpectation = async (route: DeliveryRoute) => {
    this.isRouteExpectationLoading = true;

    const registerRouteExpectationApi = store.commonStore.isGuestParcelCreation
      ? agent.Guest.registerRouteExpectation
      : agent.Shipment.registerRouteExpectation;

    try {
      await registerRouteExpectationApi(route);
    } catch (error) {
      handleError(error);
    } finally {
      runInAction(() => {
        this.isRouteExpectationLoading = false;
      });
    }
  };

  handleDeliveryOptionsResponse = async (
    data: DelivertyOptionsResponse | undefined
  ) => {
    if (!data) {
      throw new Error(
        "Info about delivery options wasn't obtained from the API!"
      );
    }

    const { miles, grouped_prices, static_time_strings } = data;

    if (miles?.length === 0) {
      throw new Error(
        "Info about delivery options (miles array) wasn't obtained from the API!"
      );
    }

    miles.forEach((option) => {
      option.uiKey = uuid();

      option.first_miles.forEach((x) => {
        x.uiKey = uuid();
      });

      option.last_miles.forEach((x) => {
        x.uiKey = uuid();
      });

      // preselect cheapeset first and last miles
      const mode = getSetting(
        this.countryDeparture?.code,
        'delivery_options',
        'delivery_options_default'
      );
      option.selectedFirstMile = getDefaultMile(option.first_miles, mode);
      option.selectedLastMile = getDefaultMile(option.last_miles, mode);

      // Adaptation for domestic. Don't ask me about this approach with 'undefined'. This was decided by backend and mobile teams.
      if (option.middle_mile.delivery_method.toString() === 'undefined') {
        option.middle_mile.delivery_method = 'car';
      }
    });

    runInAction(() => {
      this.deliveryOptions = miles;
      this.groupedDeliveryPrices = grouped_prices;
      this.staticDeliveryTimeStrings = static_time_strings;
      this.isDeliveryOptionsFailed = false;

      const middleMiles = Array.from(
        new Set(miles?.map((item) => item.middle_mile.delivery_method))
      );
      if (middleMiles.length === 1) {
        this.setMiddleMileFilter(middleMiles[0]);
      }

      if (
        this.deliveryOptions &&
        this.deliveryOptions.length === 1 &&
        !this.selectedDeliveryOption
      ) {
        this.setDeliveryOption(this.deliveryOptions[0]);
      }
    });

    // Search for city by zip-code in case user entered zip-code manually

    // Departure (From)
    if (!this.countryDeparture?.city && this.countryDeparture?.zipCode) {
      const departurePlaceDetails = await getAddressComponentsByZipCode(
        this.countryDeparture.zipCode,
        this.countryDeparture.code,
        {
          includeCity: true,
          includeRegion: true,
        }
      );
      runInAction(() => {
        if (departurePlaceDetails && this.countryDeparture) {
          const { city, region } = departurePlaceDetails;
          this.countryDeparture.city = city;
          this.countryDeparture.region = region;
        }
      });
    }

    // Destination (To)
    if (!this.countryDestination?.city && this.countryDestination?.zipCode) {
      const destinationDetails = await getAddressComponentsByZipCode(
        this.countryDestination.zipCode,
        this.countryDestination.code,
        {
          includeCity: true,
          includeRegion: true,
        }
      );
      runInAction(() => {
        if (destinationDetails && this.countryDestination) {
          const { city, region } = destinationDetails;
          this.countryDestination.city = city;
          this.countryDestination.region = region;
        }
      });
    }
  };

  prepareSenderDetails = () => {
    const contactInfoFields: ContactInfoFields = {
      email: this.shipment?.sender?.email ?? '',
      phone: this.shipment?.sender?.phone?.phone_number ?? '',
      phone_country_code: this.shipment?.sender?.phone?.country_code ?? '',
    };

    const addressFields: AddressFields = {
      country: this.shipment?.sender?.country,
      post_code: this.shipment?.sender?.address?.post_code ?? '',
      region: this.shipment?.sender?.address?.region ?? '',
      city: this.shipment?.sender?.address?.city ?? '',
      street: this.shipment?.sender?.address?.street ?? '',
      building: this.shipment?.sender?.address?.building ?? '',
      section: this.shipment?.sender?.address?.section ?? '',
      apartment: this.shipment?.sender?.address?.apartment ?? '',
      buzz_code: this.shipment?.sender?.address?.buzz_code ?? '',
      note: this.shipment?.sender?.address?.note ?? '',
      full_address: this.shipment?.sender?.address?.full_address,
    };

    const isConcierge =
      this.navigation.current?.name === 'sender-details-concierge';

    const courierPickupFields: CourierPickupFields | undefined = !isConcierge
      ? {
          courier_pickup: {
            time_to: this.shipment?.sender?.courier_pickup?.time_to ?? '',
            date: this.shipment?.sender?.courier_pickup?.date ?? '',
            timezone: this.shipment?.sender?.courier_pickup?.timezone ?? '',
            time_from: this.shipment?.sender?.courier_pickup?.time_from ?? '',
          },
        }
      : undefined;

    return {
      personalDataFields: this.senderPersonalDataFormValues,
      contactInfoFields,
      addressFields,
      courierPickupFields,
    };
  };

  handleUnprocessableAddress = (error: any) => {
    const isValidationError =
      error instanceof AxiosError &&
      (error.response?.status === ApiResponseStatus.VALIDATION_ERROR ||
        error.response?.status === ApiResponseStatus.PRECONDITION_FAILED);
    this.setSenderAddressInvalid(isValidationError);
  };
}
